import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserView } from 'react-device-detect';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  ModalWrapper, ModalContent, LeftContainer, RightContainer, AlmaLogoBlack, InfoImg, HeaderWrapper, ParagraphTitle, ParagraphDescription,
  ParagraphVersion, ContentWrapper, LeftContentContainer, RightContentContainer, StyledIcon, IconWithDescriptionContainer, ParagraphContainer,
  ParagraphContentTitleBold, ParagraphContentDescription, ParagraphContentTitle, ParagraphContentAddress, ParagraphContentAddressBold,
} from './styled/StyledInfoModal';
import versionPackage from '../../../../package.json';
import { addSubdirectoryToPath } from '../../../util/getConfig';

const LicenseInfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    margin-top: 15px;
`;

const LeftLicenseBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-left: 12px;
`;

const RightVersionBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
`;

const LicenseField = styled(ParagraphVersion)`
    margin-bottom: 2px;
    white-space: nowrap;
`;

const InfoModal = ({ show, onClose }) => {
  const modalRef = useRef(null);
  const almaInfoPlanet = addSubdirectoryToPath('/meta/images/infoModal/alma-info-planet.png');
  const almaClinicLogoBlack = addSubdirectoryToPath('/meta/images/infoModal/alma-clinic-logo-black.png');
  const warningIcon = addSubdirectoryToPath('/meta/images/infoModal/warning-icon.png');
  const urlIcon = addSubdirectoryToPath('/meta/images/infoModal/url-icon.png');
  const factoryIcon = addSubdirectoryToPath('/meta/images/infoModal/factory-icon.png');
  const medicalDeviceIcon = addSubdirectoryToPath('/meta/images/infoModal/medical-device-icon.png');
  const ceIcon = addSubdirectoryToPath('/meta/images/infoModal/ce-icon.png');
  const factoryWhiteIcon = addSubdirectoryToPath('/meta/images/infoModal/factory-white-icon.png');
  const udiIcon = addSubdirectoryToPath('/meta/images/infoModal/udi-icon.png');
  const { t } = useTranslation();

  const config = useSelector((state) => state.config);
  const { USER: client, licenseID: licenseID, licenseExpirationDate } = config;

  let productionBuildDate = PRODUCTION_BUILD_DATE || '0000 - 00';
  const [mainVersion, buildNumber] = versionPackage.version.split('-');
  const formattedVersion = [
    mainVersion
      .split('.')
      .map((part) => part.padStart(2, '0'))
      .join(''),
    buildNumber ? buildNumber.padStart(2, '0') : '00',
  ].join('');

  const openLink = () => {
    window.open('https://www.alma-medical.com', '_blank');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleTouchOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleTouchOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleTouchOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleTouchOutside);
    };
  }, [show, onClose]);

  return (
    <ModalWrapper show={show}>
      <ModalContent ref={modalRef}>
        <BrowserView>
          <LeftContainer>
            <InfoImg src={almaInfoPlanet} alt="alma Info Img" />
          </LeftContainer>
        </BrowserView>
        <RightContainer>
          <HeaderWrapper>
            <LicenseInfoRow>
              <LeftLicenseBlock>
                {client && (
                  <LicenseField>
                    {t('client')} <b>{typeof client === 'object' ? JSON.stringify(client) : String(client)}</b>
                  </LicenseField>
                )}
                {licenseID && (
                  <LicenseField>
                    {t('license.id')} <b>{typeof licenseID === 'object' ? JSON.stringify(licenseID) : String(licenseID)}</b>
                  </LicenseField>
                )}
                {licenseExpirationDate && (
                  <LicenseField>
                    {t('license.date.expired')} <b>{new Date(licenseExpirationDate).toLocaleDateString()}</b>
                  </LicenseField>
                )}
              </LeftLicenseBlock>
              <RightVersionBlock>
                <AlmaLogoBlack src={almaClinicLogoBlack} alt="alma Clinic Logo Black" />
                <ParagraphTitle>{t('infoModalTitle')}</ParagraphTitle>
                <ParagraphDescription>{t('infoModalDescription')}</ParagraphDescription>
                <ParagraphVersion>{t('infoModalVersionTitle')} <b>{versionPackage.version.replace('-', '.')}</b></ParagraphVersion>
              </RightVersionBlock>
            </LicenseInfoRow>
          </HeaderWrapper>
          <ContentWrapper>
            <LeftContentContainer>
              <IconWithDescriptionContainer>
                <StyledIcon src={warningIcon} alt="Warning Icon" />
                <ParagraphContentTitleBold>{t('infoModalWarning')}</ParagraphContentTitleBold>
              </IconWithDescriptionContainer>
              <ParagraphContentDescription>{t('infoModalWarningDescription1')}</ParagraphContentDescription>
              <ParagraphContentDescription>{t('infoModalWarningDescription2')}</ParagraphContentDescription>
              <ParagraphContentDescription>{t('infoModalWarningDescription3')}</ParagraphContentDescription>
              <IconWithDescriptionContainer onClick={openLink} target="_blank" rel="noreferrer">
                <StyledIcon src={urlIcon} alt="Url Icon" />
                <ParagraphContentTitle>www.alma-medical.com</ParagraphContentTitle>
              </IconWithDescriptionContainer>
              <IconWithDescriptionContainer>
                <StyledIcon src={factoryIcon} alt="Factory Icon" />
                <ParagraphContainer>
                  <ParagraphContentAddressBold>ALMA IT SYSTEMS, S.L.</ParagraphContentAddressBold>
                  <ParagraphContentAddress>C/ Mestre Dalmau, 19 (08031 Barcelona) · Tel. +34 93 238 05 92 info@alma-medical.com © 2026 All rights reserved.</ParagraphContentAddress>
                </ParagraphContainer>
              </IconWithDescriptionContainer>
            </LeftContentContainer>
            <RightContentContainer>
              <IconWithDescriptionContainer>
                <StyledIcon src={medicalDeviceIcon} alt="Medical Device Icon" />
                <ParagraphContentTitle>{t('infoModalWarning2')}</ParagraphContentTitle>
              </IconWithDescriptionContainer>
              <IconWithDescriptionContainer>
                <StyledIcon src={ceIcon} alt="Ce Icon" />
              </IconWithDescriptionContainer>
              <IconWithDescriptionContainer>
                <StyledIcon src={factoryWhiteIcon} alt="Factory White Icon" />
                <ParagraphContentTitle>{productionBuildDate}</ParagraphContentTitle>
              </IconWithDescriptionContainer>
              <IconWithDescriptionContainer>
                <StyledIcon src={udiIcon} alt="UDI Icon" />
                <ParagraphContainer>
                  <ParagraphContentTitle>(01) 08437020517003</ParagraphContentTitle>
                  <ParagraphContentTitle>(11) {productionBuildDate.replace(/[\s-]/g, '')}</ParagraphContentTitle>
                  <ParagraphContentTitle>(8012) {formattedVersion}</ParagraphContentTitle>
                </ParagraphContainer>
              </IconWithDescriptionContainer>
            </RightContentContainer>
          </ContentWrapper>
        </RightContainer>
      </ModalContent>
    </ModalWrapper>
  );
};

export default InfoModal;
